import React, { useCallback } from 'react';
import {
  Box,
  FormControl,
  MenuItem,
  IconButton,
  Typography,
  Tooltip,
  useTheme,
  useMediaQuery,
  Stack,
  FormControlLabel,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import Select from '../UIKit/Select';
import Switch from '../UIKit/Switch';
import SearchInput from '../UIKit/SearchInput';

const LeaderboardFilters = ({
  filters,
  onFilterChange,
  onReset,
  courses = [],
  expanded = false,
  onExpandToggle,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation(['leaderboard', 'common']);

  const handleSearchChange = useCallback((value) => {
    onFilterChange('searchQuery', value);
  }, [onFilterChange]);

  const handleChange = useCallback((key, value) => {
    onFilterChange(key, value);
  }, [onFilterChange]);

  // Мемоизированные обработчики для Select компонентов
  const handleTimeFrameChange = useCallback((e) => handleChange('timeFrame', e.target.value), [handleChange]);
  const handleFilterChange = useCallback((e) => handleChange('filter', e.target.value), [handleChange]);
  const handleSpecificIdChange = useCallback((e) => handleChange('specificId', e.target.value), [handleChange]);
  const handleRoleFilterChange = useCallback((e) => handleChange('roleFilter', e.target.value), [handleChange]);
  const handleExcludeInactiveChange = useCallback((e) => handleChange('excludeInactive', e.target.checked), [handleChange]);

  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
        borderRadius: '16px',
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: expanded ? 2 : 0,
        }}
      >
        <Typography variant="h6" display="flex" alignItems="center">
          <FilterListIcon sx={{ mr: 1 }} />
          {t('leaderboard:filters.title')}
        </Typography>

        <Stack direction="row" spacing={1}>
          <Tooltip title={t('leaderboard:filters.refresh')}>
            <IconButton onClick={onReset} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              expanded
                ? t('leaderboard:filters.collapse')
                : t('leaderboard:filters.expand')
            }
          >
            <IconButton onClick={onExpandToggle} size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {expanded && (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: 2,
              mb: 2,
            }}
          >
            <FormControl fullWidth variant="outlined" size="small">
              <Select
                value={filters.timeFrame}
                onChange={handleTimeFrameChange}
                label={t('leaderboard:filters.timeFrame')}
              >
                <MenuItem value="all-time">
                  {t('leaderboard:filters.allTime')}
                </MenuItem>
                <MenuItem value="daily">
                  {t('leaderboard:filters.daily')}
                </MenuItem>
                <MenuItem value="weekly">
                  {t('leaderboard:filters.weekly')}
                </MenuItem>
                <MenuItem value="monthly">
                  {t('leaderboard:filters.monthly')}
                </MenuItem>
                <MenuItem value="quarterly">
                  {t('leaderboard:filters.quarterly')}
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth variant="outlined" size="small">
              <Select
                value={filters.filter}
                onChange={handleFilterChange}
                label={t('leaderboard:filters.contentType')}
              >
                <MenuItem value="all">{t('leaderboard:filters.all')}</MenuItem>
                <MenuItem value="courses">
                  {t('leaderboard:filters.courses')}
                </MenuItem>
                <MenuItem value="tests">
                  {t('leaderboard:filters.tests')}
                </MenuItem>
                {courses.length > 0 && (
                  <MenuItem value="specific-course">
                    {t('leaderboard:filters.specificCourse')}
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {filters.filter === 'specific-course' && (
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  value={filters.specificId || ''}
                  onChange={handleSpecificIdChange}
                  label={t('leaderboard:filters.selectCourse')}
                >
                  {courses.map((course) => (
                    <MenuItem key={course._id} value={course._id}>
                      {course.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl fullWidth variant="outlined" size="small">
              <Select
                value={filters.roleFilter}
                onChange={handleRoleFilterChange}
                label={t('leaderboard:filters.role')}
              >
                <MenuItem value="all">
                  {t('leaderboard:filters.allRoles')}
                </MenuItem>
                <MenuItem value="student">
                  {t('common:roles.student')}
                </MenuItem>
                <MenuItem value="teacher">
                  {t('common:roles.teacher')}
                </MenuItem>
                <MenuItem value="admin">{t('common:roles.admin')}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <SearchInput
              value={filters.searchQuery}
              onChange={handleSearchChange}
              placeholder={t('leaderboard:filters.searchPlaceholder')}
            />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: isMobile ? '100%' : 'auto',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.excludeInactive !== false}
                    onChange={handleExcludeInactiveChange}
                    color="primary"
                    sx={{ mx: 1 }}
                  />
                }
                label={t('leaderboard:filters.hideInactive')}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default LeaderboardFilters;
